import React, { Component } from 'react';
import moment from 'moment';
import { every, isEmpty } from 'lodash';
import { Button } from 'ui';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import SessionForm from 'components/InPersonTraining/shared/SessionForm';
import ZoomContext from 'components/InPersonTraining/shared/ZoomContext';
import Error from 'components/InPersonTraining/shared/Error';
import ErrorPage from 'components/shared/ErrorPage';
import TimeZone from 'components/InPersonTraining/shared/SessionForm/TimeZone';
import { appendTimeToObject } from 'components/InPersonTraining/shared/utilities/SessionTime';
import { isValidURL } from 'utils/string_utils';
import Loading from 'components/shared/Loading';
import Description from './Description';
import Name from './Name';

class NewEvent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingCache: true,
      loading: true,
      pageError: false,
      name: '',
      description: '',
      parentId: '',
      session: {
        trainerId: null,
        startTime: moment().add(1, 'hour').startOf('hour'),
        endTime: moment().add(2, 'hour').startOf('hour'),
        timeZone: TimeZone.getLastSelected(),
        offSet: '',
        capOption: false,
        maxAttendees: '',
        location: '',
        videoConferencing: { type: 'none', url: '', id: null },
      },
      error: null,
      meta: {
        users: [],
        zoomClientId: null,
        currentUser: {},
        currentCompany: {},
        timeZones: [],
      },
    };
  }

  componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const formStateKey = urlParams.get('formStateKey');

    request
      .get(routes.IN_PERSON_TRAINING.NEW_EVENT)
      .then(payload => {
        this.setState(prevState => ({
          ...payload,
          session: {
            ...prevState.session,
            trainerId: payload.meta.currentUser.id,
          },
          name: urlParams.get('event_name') || '',
          description: urlParams.get('event_description') || '',
          parentId: urlParams.get('event_parent_id') || '',
          loading: false,
        }));
      })
      .catch(() => {
        this.setState({ pageError: true, loading: false });
      });

    if (formStateKey) {
      this.setStateFromCacheKey(formStateKey);
    } else {
      this.setState({ loadingCache: false });
    }
  }

  get isValid() {
    return every([
      !!this.state.name.trim(),
      moment(this.state.session.startTime).isValid(),
      moment(this.state.session.endTime).isValid(),
      !isEmpty(this.state.session.timeZone),
      this.isVideoConferencingValid(),
      this.isZoomTimeValid(),
    ]);
  }

  isVideoConferencingValid() {
    const { type, url } = this.state.session.videoConferencing;

    if (type !== 'other') return true;

    return isValidURL(url);
  }

  isZoomTimeValid() {
    const timeZoneOffSet = this.state.meta.timeZones.find(
      ({ name }) => this.state.session.timeZone === name,
    )?.offset;
    const startTimeWithOffSet = moment(
      `${this.state.session.startTime.format('YYYY-MM-DDTHH:mm:ss')}${
        timeZoneOffSet === '' || timeZoneOffSet === undefined
          ? '+00:00'
          : timeZoneOffSet
      }`,
    );
    const isStartBeforeCurrent = startTimeWithOffSet.isBefore(moment());
    const { type } = this.state.session.videoConferencing;
    let isValid = true;
    if (type === 'zoom' && isStartBeforeCurrent) {
      isValid = false;
    }

    return isValid;
  }

  handleInputChange = e => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleSessionInputChange = e => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      session: { ...prevState.session, [name]: value },
    }));
  };

  handleEndTimeChange = endTime => {
    this.setState(prevState => ({
      session: { ...prevState.session, endTime },
    }));
  };

  handleStartTimeChange = date => {
    this.setState(prevState => ({
      session: {
        ...prevState.session,
        startTime: date,
        endTime: moment(date).set({
          hour: moment(prevState.session.endTime).get('hour'),
          minute: moment(prevState.session.endTime).get('minute'),
        }),
      },
    }));
  };

  handleTimeZoneChange = (timeZone, timeZoneOffSet) => {
    this.setState(
      prevState => ({
        session: { ...prevState.session, timeZone, offSet: timeZoneOffSet },
      }),
      () => {
        TimeZone.setLastSelected(timeZone);
      },
    );
  };

  handleTrainerChange = trainerId => {
    this.setState(prevState => ({
      session: { ...prevState.session, trainerId },
    }));
  };

  resetMaxAttendees(capOption) {
    if (capOption) return;
    if (!isEmpty(this.state.session.maxAttendees))
      this.setState(prevState => ({
        session: { ...prevState.session, maxAttendees: '' },
      }));
  }

  handleCapOptionsChange = e => {
    const capOption = JSON.parse(e.target.value);
    this.setState(
      prevState => ({
        session: { ...prevState.session, capOption },
      }),
      () => {
        this.resetMaxAttendees(capOption);
      },
    );
  };

  handleVideoConferenceChange = data => {
    this.setState(prevState => ({
      session: { ...prevState.session, videoConferencing: data },
    }));
  };

  cacheFormState = () => {
    const { state } = this;
    return request
      .postJSON('/current_user/cache', state)
      .then(response => response.key);
  };

  setStateFromCacheKey = formStateKey => {
    return request.get(`/current_user/cache/${formStateKey}`).then(data => {
      if (data) {
        const { session } = data;

        if (!session) {
          this.setState(data);
        } else {
          const newSessionData = {
            ...session,
            startTime: moment(session.startTime),
            endTime: moment(session.endTime),
          };
          this.setState({
            ...data,
            session: newSessionData,
            loadingCache: false,
          });
        }
      }
    });
  };

  handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    request
      .postJSON(routes.IN_PERSON_TRAINING.EVENTS, {
        event: {
          name: this.state.name,
          description: this.state.description,
          parentId: this.state.parentId,
        },
        session: appendTimeToObject({
          object: this.state.session,
          startTime: this.state.session.startTime,
          endTime: this.state.session.endTime,
        }),
      })
      .then(res => {
        if (res.success) {
          window.location = routes.IN_PERSON_TRAINING.EVENT(res.id);
        }
      })
      .catch(error => {
        const { responseJSON, responseText } = error;
        this.setState({ error: responseJSON || responseText });
      });
  };

  render() {
    if (this.state.pageError) {
      return <ErrorPage />;
    }
    const { name, description, session, error } = this.state;
    const { videoConferencing } = session;
    const isZoom = videoConferencing && videoConferencing.type === 'zoom';

    const { users, timeZones, currentUser, currentCompany, zoomClientId } =
      this.state.meta;
    return (
      <Loading loading={this.state.loading}>
        <ZoomContext.Provider
          value={{ zoomClientId, cacheFormState: this.cacheFormState }}
        >
          <form
            onSubmit={this.handleSubmit}
            className="NewEventForm ff-ss mt-32"
          >
            <h1 className="ff-ss mb-32">
              {t('in_person_training.new_event.heading')}
            </h1>
            <Name value={name} onChange={this.handleInputChange} />
            <Description
              value={description}
              onChange={this.handleInputChange}
            />
            <div className="NewEventForm-sessionForm">
              {!this.state.loadingCache && (
                <SessionForm
                  session={session}
                  users={users}
                  currentUser={currentUser}
                  currentCompany={currentCompany}
                  timeZones={timeZones}
                  canEditTime
                  handleStartTimeChange={this.handleStartTimeChange}
                  handleEndTimeChange={this.handleEndTimeChange}
                  handleTimeZoneChange={this.handleTimeZoneChange}
                  handleTrainerChange={this.handleTrainerChange}
                  handleCapOptionsChange={this.handleCapOptionsChange}
                  handleSessionInputChange={this.handleSessionInputChange}
                  handleVideoConferenceChange={this.handleVideoConferenceChange}
                />
              )}
            </div>
            {error && <Error {...error} />}
            <Button
              customColor
              tall
              submit
              disabled={!this.isValid}
              ariaLabel={
                isZoom
                  ? t('in_person_training.new_event.create_event_with_zoom')
                  : t('in_person_training.new_event.create_event')
              }
            >
              {isZoom
                ? t('in_person_training.new_event.create_event_with_zoom')
                : t('in_person_training.new_event.create_event')}
            </Button>
          </form>
        </ZoomContext.Provider>
      </Loading>
    );
  }
}

export default NewEvent;
